import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import { StreamChat } from "stream-chat";
import { StreamVideoClient } from "@stream-io/video-react-sdk";
import { ENV } from "@/config/env";
import { useAuth } from "@/features/auth/context/AuthContext";
import { streamService } from "@/features/chat/services/streamService";
import { storage } from "@/shared/utils/storage";
import { formatDisplayName } from "@/shared/utils/formatters";
import { STORAGE_KEYS } from "@/config/constants";

interface StreamChatContextType {
  chatClient: StreamChat | null;
  videoClient: StreamVideoClient | null;
  isReady: boolean;
  unreadChatCount: number;
}

const StreamChatContext = createContext<StreamChatContextType>({
  chatClient: null,
  videoClient: null,
  isReady: false,
  unreadChatCount: 0,
});

export const useStreamChat = () => useContext(StreamChatContext);

export const StreamChatProvider = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated, userData } = useAuth();
  const [chatClient, setChatClient] = useState<StreamChat | null>(null);
  const [videoClient, setVideoClient] = useState<StreamVideoClient | null>(null);
  const [isReady, setIsReady] = useState(false);
  const [unreadChatCount, setUnreadChatCount] = useState(0);
  const clientRef = useRef<StreamChat | null>(null);
  const videoClientRef = useRef<StreamVideoClient | null>(null);

  useEffect(() => {
    if (!isAuthenticated || !userData) {
      // Disconnect if logged out
      if (clientRef.current?.userID) {
        clientRef.current.disconnectUser();
        clientRef.current = null;
        setChatClient(null);
      }
      if (videoClientRef.current) {
        videoClientRef.current.disconnectUser();
        videoClientRef.current = null;
        setVideoClient(null);
      }
      setIsReady(true);
      return;
    }

    let isMounted = true;

    const connect = async () => {
      try {
        const client = StreamChat.getInstance(ENV.STREAM_API_KEY);
        const userId = userData.id.toString();

        const fullName = formatDisplayName(userData.lastName, userData.firstName, userData.middleName);
        const userPayload = {
          id: userId,
          name: fullName,
          // Permanent avatar proxy — never expires, safe to store in Stream.
          image: userData.profilePicId
            ? `${ENV.API_URL}/profile/avatar/${userData.id}`
            : userData.profileImage || "",
        };

        if (client.userID && client.userID !== userId) {
          await client.disconnectUser();
        }

        // Async token provider — the SDK calls this automatically on
        // initial connect AND whenever the token expires / WS reconnects.
        const tokenProvider = async () => {
          const freshToken = await streamService.getToken();
          storage.set(STORAGE_KEYS.STREAM_TOKEN, freshToken);
          return freshToken;
        };

        if (!client.userID) {
          await client.connectUser(userPayload, tokenProvider);
        }

        clientRef.current = client;

        // Initialize video client with the same token provider
        try {
          const vc = StreamVideoClient.getOrCreateInstance({
            apiKey: ENV.STREAM_API_KEY,
            user: userPayload,
            tokenProvider,
            options: {
              // video-client >= 1.44 remembers the last mic/camera on-off state
              // in localStorage and re-applies it on the next join. Calls here
              // always start camera-off / mic-on (ActiveCallContext), so keep
              // that deterministic rather than "whatever the last call ended as".
              devicePersistence: { enabled: false },
            },
          });
          videoClientRef.current = vc;
          if (isMounted) setVideoClient(vc);
        } catch (videoErr) {
          console.error("[StreamChat] Video client init failed:", videoErr);
        }

        if (isMounted) {
          setChatClient(client);
          setIsReady(true);
        }
      } catch (err) {
        console.error("[StreamChat] Connection error:", err);
        if (isMounted) setIsReady(true);
      }
    };

    connect();

    return () => {
      isMounted = false;
    };
  }, [isAuthenticated, userData?.id]);

  // ─── Track total unread chat message count ───
  useEffect(() => {
    if (!chatClient) {
      setUnreadChatCount(0);
      return;
    }

    // Seed initial count
    chatClient.getUnreadCount().then((res) => {
      setUnreadChatCount(res.total_unread_count ?? 0);
    }).catch((err) => {
      console.warn("[StreamChat] Failed to get initial unread count:", err);
    });

    // These events carry total_unread_count in the payload
    const handleUnreadUpdate = (event: any) => {
      if (typeof event.total_unread_count === "number") {
        setUnreadChatCount(event.total_unread_count);
      }
    };

    // message.read fires when watching a channel (no global count in payload)
    const handleMessageRead = () => {
      chatClient.getUnreadCount().then((res) => {
        setUnreadChatCount(res.total_unread_count ?? 0);
      }).catch(() => {});
    };

    const subs = [
      chatClient.on("notification.message_new", handleUnreadUpdate),
      chatClient.on("message.new", handleUnreadUpdate),
      chatClient.on("notification.mark_read", handleUnreadUpdate),
      chatClient.on("notification.mark_unread", handleUnreadUpdate),
      chatClient.on("message.read", handleMessageRead),
    ];

    return () => {
      subs.forEach((sub) => sub.unsubscribe());
    };
  }, [chatClient]);

  return (
    <StreamChatContext.Provider value={{ chatClient, videoClient, isReady, unreadChatCount }}>
      {children}
    </StreamChatContext.Provider>
  );
};
